import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	CUSTOM_ELEMENTS_SCHEMA,
	Optional,
	signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { of, Subject } from 'rxjs';
import { take, takeUntil, finalize, filter, catchError } from 'rxjs/operators';
import { ActivityFormService } from '../activity-form/activity-form.service';
import { CONTENT_TYPES, MODES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { GoCdkPopoverDirective } from '@go-modules/directives/go-cdk-popover/go-cdk-popover.directive';
import { LibraryService } from '@go-modules/services/library/library.service';
import { PanelModule } from '@go-modules/components/panel/panel.module';
import { NgxToolbarModule } from '@go-modules/components/toolbar/toolbar.module';
import { NgxToolbarButtonModule } from '@go-modules/components/toolbar/toolbar-button/toolbar-button.module';
import { Action } from '@go-modules/components/toolbar/toolbar-button/action-to-icon-class-map';
import { rubricEditorModal, rubricEditorModalToken } from 'go-modules/modals/rubric-editor/modal.factory';
import { RubricTemplate } from '@go-modules/interfaces/rubrics/rubric';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { MessageDialogComponent } from '@go-modules/components/dialogs/message-dialog/message-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { MatIconModule } from '@angular/material/icon';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import {
	type LibraryDialogContext
} from '@go-modules/components/dialogs/library-dialog/library-dialog.component';
import { isDismissedModalResult } from '@go-modules/utilities';
import { ActivityEditAccessibilityAnnouncerService } from '../activity-edit-accessibility-announcer.service';
import {
	ACTIVITY_EDITOR_CONTEXT,
	ActivityEditorContext
} from '../../../dialogs/activity-preview-editor/activity-editor-context';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { ProductTier } from '@go-modules/interfaces/licenses/product-tier';
import type { AiMarkersEntitlement } from '@go-modules/interfaces/ai-markers-entitlement';
import type { License } from '@go-modules/interfaces/licenses';
import { UserService as NgxUserService } from '@go-modules/services/user/user.service';

interface GradingType {
	title: string;
	description: string;
	disabled?: boolean;
	needsTranslated?: boolean;
}

interface GradingTypeLookup {
	NOT_GRADED: GradingType;
	POINTS: GradingType;
	INSTRUCTOR_RUBRIC: GradingType;
}

type GradingTypeKey = keyof GradingTypeLookup;
interface ActivityTemplateSelection {
	slug?: string;
	getType?: () => string;
}
interface ActivityEditorGoSettings {
	currentFolder?: {
		license?: License;
	} | null;
}
const AUTO_GRADING_UNSUPPORTED_TEMPLATE_TYPES = ['feedback_only', 'conversation_board'];

export const RUBRIC_VIEWER_PREVIEW_MODE = 'live';
export const RUBRIC_VIEWER_EDIT_MODE = 'edit';

@Component({
	selector: 'activity-grading',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [
		CommonModule,
		ReactiveFormsModule,
		TranslateModule,
		MatFormFieldModule,
		MatSelectModule,
		MatOptionModule,
		MatInputModule,
		MatTooltipModule,
		MatCheckboxModule,
		GoCdkPopoverDirective,
		PanelModule,
		NgxToolbarModule,
		NgxToolbarButtonModule,
		MatIconModule
	],
	templateUrl: './activity-grading.component.html',
	styleUrls: ['./activity-grading.component.scss']
})
export class ActivityGradingComponent implements OnInit, OnDestroy {
	@Input() public activity: ActivityModel;
	@Input()
	public set selectedTemplate (template: ActivityTemplateSelection | null) {
		this.selectedTemplateSelection = template;
		this.syncAutoGradingControlsWithEligibility();
	}

	public get selectedTemplate (): ActivityTemplateSelection | null {
		return this.selectedTemplateSelection;
	}

	@Input() public rubricRole: string;
	public gradingTypeLookup: GradingTypeLookup = {
		NOT_GRADED: {
			title: 'activity-editor-new_not-graded',
			description: 'activity-editor-new_not-graded-description'
		},
		POINTS: {
			title: 'activity-editor-new_points',
			description: 'activity-editor-new_points-description'
		},
		INSTRUCTOR_RUBRIC: {
			title: 'activity-editor-new_instructor-rubrics',
			description: 'activity-editor-new_instructor-rubrics-description'
		}
	};

	public gradingOptions: { key: GradingTypeKey; config: GradingType }[] = [];
	public gradingTypeControl: FormControl<GradingTypeKey>;
	public selectedRubric: RubricTemplate | null = null;
	public isLoadingRubric: boolean = false;
	public reviewersAccessControl: FormControl | null = null;
	public autoGradingControl: FormControl | null = null;
	public autoGradingUseAIScoreControl: FormControl | null = null;
	public autoGradingAllowReviewerOverrideControl: FormControl | null = null;
	public autoGradingAutoPostAIRubricControl: FormControl | null = null;
	public aiContextControl: FormControl | null = null;
	public pointsControl: FormControl | null = null;
	public toolbarActions = Action;
	public selectedGradingTypeKey: GradingTypeKey = 'NOT_GRADED';
	public rubricTemplateControl: FormControl | null = null;
	private destroy$ = new Subject<void>();
	private isDestroyed = false;
	private selectedTemplateSelection: ActivityTemplateSelection | null = null;
	private readonly advancedLicenseEntitlement = signal<AiMarkersEntitlement | null>(null);
	private readonly advancedLicenseEntitlementResolved = signal(false);

	constructor (
		public activityFormService: ActivityFormService,
		public featureFlag: NgxFeatureFlagService,
		private cdr: ChangeDetectorRef,
		private translate: TranslateService,
		private libraryService: LibraryService,
		private matDialog: MatDialog,
		private ngxGoModalService: GoModalService,
		private liveAnnouncer: LiveAnnouncer,
		private ngxUserService: NgxUserService,
		@Inject(rubricEditorModalToken) private rubricEditor: ReturnType<typeof rubricEditorModal>,
		@Optional() @Inject(ACTIVITY_EDITOR_CONTEXT) private editorContext: ActivityEditorContext | null,
		@Optional() private activityEditAccessibilityAnnouncer?: ActivityEditAccessibilityAnnouncerService
	) {
		this.gradingTypeControl = new FormControl<GradingTypeKey>('NOT_GRADED', { nonNullable: true });
		this.gradingTypeControl.addValidators(Validators.required);
	}

	public ngOnInit (): void {
		this.updateUserAdvancedLicenseEntitlement();

		this.pointsControl = this.activityFormService.getControl('total_score');
		this.rubricTemplateControl = this.activityFormService.getControl('rubric_template_id');
		this.reviewersAccessControl = this.activityFormService.getControl('can_reviewers_see_grades');
		this.autoGradingControl = this.activityFormService.getControl('ai_graded_rubric_enabled');
		this.autoGradingUseAIScoreControl = this.activityFormService.getControl('use_ai_score');
		this.autoGradingAllowReviewerOverrideControl = this.activityFormService.getControl('allow_reviewer_score_overrides');
		this.autoGradingAutoPostAIRubricControl = this.activityFormService.getControl('automatically_post_ai_rubric');
		this.aiContextControl = this.activityFormService.getControl('ai_context');
		this.syncAutoGradingControlsWithEligibility();

		this.gradingTypeLookup.NOT_GRADED.disabled = this.isPointsReadonly;
		this.gradingOptions = [
			{ key: 'NOT_GRADED', config: this.gradingTypeLookup.NOT_GRADED },
			{ key: 'POINTS', config: this.gradingTypeLookup.POINTS },
			{ key: 'INSTRUCTOR_RUBRIC', config: this.gradingTypeLookup.INSTRUCTOR_RUBRIC }
		];

		const initialType = this.determineInitiallySelectedGradingType();
		this.selectedGradingTypeKey = initialType;
		this.gradingTypeControl.setValue(initialType, { emitEvent: false });
		this.updatePointsValidators();

		if (initialType === 'INSTRUCTOR_RUBRIC' && this.activity?.rubric_template_id) {
			this.loadSelectedRubric(this.activity.rubric_template_id);
		}

		// if rubric is in use then only show rubric grading type option
		// or else users can bypass rubric lockdown
		if (this.activity.rubric_template_id) {
			this.libraryService.checkRubricUsage(this.activity.rubric_template_id)
				.pipe(take(1))
				.subscribe((response) => {
					if (response.in_use && response.activities.includes(this.activity.activity_id)) {
						this.gradingOptions = [
							{ key: 'INSTRUCTOR_RUBRIC', config: this.gradingTypeLookup.INSTRUCTOR_RUBRIC }
						];
					}
				});
		}

		this.gradingTypeControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((value) => {
				if (value) {
					this.onGradingTypeSelectionChange(value);
				}
			});

		this.autoGradingControl.valueChanges
			.pipe(
				filter((checked) => !checked),
				takeUntil(this.destroy$)
			)
			.subscribe(() => {
				this.autoGradingUseAIScoreControl.setValue(false);
				this.autoGradingAllowReviewerOverrideControl.setValue(false);
				this.autoGradingAutoPostAIRubricControl.setValue(false);
			});
	}

	public ngOnDestroy (): void {
		this.isDestroyed = true;
		this.destroy$.next();
		this.destroy$.complete();
	}

	public shouldDisplayPointsPossible (): boolean {
		if (!this.pointsControl) {
			return false;
		}

		if (this.selectedGradingTypeKey === 'INSTRUCTOR_RUBRIC') {
			const value = this.pointsControl.value;
			return value !== null && value !== undefined;
		}

		return this.selectedGradingTypeKey === 'POINTS';
	}

	public isPointsPossibleDisabled (): boolean {
		return this.selectedGradingTypeKey === 'INSTRUCTOR_RUBRIC' || this.isPointsReadonly;
	}

	public getPointsDisabledTooltip (): string {
		if (this.selectedGradingTypeKey === 'INSTRUCTOR_RUBRIC') {
			return this.translate.instant('activity-editor-header_points-generated');
		}
		return this.translate.instant('activity-editor-header_points-lms');
	}

	public shouldDisplayPointsPossibleError (): boolean {
		return this.selectedGradingTypeKey === 'POINTS' &&
			this.pointsControl.invalid &&
			(this.pointsControl.touched || this.pointsControl.dirty);
	}

	public getSelectedGradingTitle (): string {
		const selected = this.gradingOptions.find((option) => option.key === this.selectedGradingTypeKey);
		return this.translate.instant(selected.config.title);
	}

	public isGradingOptionDisabled (option: { key: GradingTypeKey; config: GradingType }): boolean {
		return option.config.disabled === true ||
			(option.key === 'INSTRUCTOR_RUBRIC' && this.isRubricSelectionUnavailable());
	}

	public getGradingOptionDescriptionKey (option: { key: GradingTypeKey; config: GradingType }): string {
		if (option.key === 'INSTRUCTOR_RUBRIC' && this.isRubricSelectionUnavailable()) {
			return 'activity-editor-new_instructor-rubrics-unavailable';
		}

		return option.config.description;
	}

	public isRubricSelectionUnavailable (): boolean {
		if (this.editorContext == null) {
			return false;
		}

		return this.editorContext.group?.() == null && this.editorContext.account?.() == null;
	}

	public normalizePointsPossible (): void {
		const value = this.pointsControl.value;
		if (value === null || value === undefined || value === '') {
			return;
		}

		const parsed = Number(value);
		if (isNaN(parsed)) {
			return;
		}

		const normalized = Math.round(parsed * 100) / 100;
		this.pointsControl.setValue(normalized, { emitEvent: false });
	}

	public handlePreview (): void {
		const selectedRubric = this.getAvailableRubric();
		if (selectedRubric == null) {
			return;
		}

		this.rubricEditor.open({
			modalData: {
				options: {
					mode: RUBRIC_VIEWER_PREVIEW_MODE,
					previewOnly: true,
					postData: {
						group_id: this.activity.group_id
					},
					schemaId: selectedRubric.id
				}
			}
		});
	}

	public shouldShowAutoGrading (): boolean {
		return this.canResolveAutoGradingEligibility() && this.hasAutoGradingEligibility();
	}

	public handleEdit (): void {
		const selectedRubric = this.getAvailableRubric();
		if (selectedRubric == null) {
			return;
		}

		this.libraryService.checkRubricUsage(selectedRubric.id)
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe((response) => {
				if (!this.isRubricStillSelected(selectedRubric)) {
					return;
				}

				// if rubric is in use then no editing allowed
				if (response.in_use) {
					// in use on this assignment
					if (response.activities.includes(this.activity.activity_id)) {
						this.matDialog.open(MessageDialogComponent, {
							data: {
								title: this.translate.instant('modal-rubric-edit_title'),
								message: this.translate.instant('modal-rubric-edit_message')
							}
						});
					// in use on another assignment
					} else {
						this.openCopyRubricModal(true, selectedRubric);
					}
				// no permission to edit
				} else if (!response.can_edit) {
					this.openCopyRubricModal(false, selectedRubric);
				// not in use anywhere can edit
				} else {
					this.rubricEditor.open({
						modalData: {
							options: {
								mode: RUBRIC_VIEWER_EDIT_MODE,
								previewOnly: false,
								postData: {
									group_id: this.activity.group_id
								},
								schemaId: selectedRubric.id
							}
						}
					}).result.then((template: RubricTemplate) => {
						if (!this.isRubricStillSelected(selectedRubric)) {
							return;
						}

						this.setSelectedRubric(template);
					});
				}
			});
	}

	public openCopyRubricModal (isInUse: boolean, selectedRubric: RubricTemplate | null = this.selectedRubric) {
		if (selectedRubric == null) {
			return;
		}

		this.matDialog.open(ConfirmDialogComponent, {
			data: {
				title: this.translate.instant('modal-rubric-edit-copy_title'),
				message: isInUse ? this.translate.instant('modal-rubric-edit-decouple_message') : this.translate.instant('modal-rubric-edit-no-permission_message'),
				confirmText: this.translate.instant('common_create-copy'),
				cancelText: this.translate.instant('common_cancel')
			}
		}).afterClosed()
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe((confirm: boolean) => {
				if (confirm) {
					if (!this.isRubricStillSelected(selectedRubric)) {
						return;
					}

					this.libraryService.copyRubricTemplate(selectedRubric.id)
						.pipe(take(1), takeUntil(this.destroy$))
						.subscribe((res) => {
							if (!this.isRubricStillSelected(selectedRubric)) {
								return;
							}

							this.selectedRubric = res;
							this.rubricEditor.open({
								modalData: {
									options: {
										mode: RUBRIC_VIEWER_EDIT_MODE,
										previewOnly: false,
										postData: {
											group_id: this.activity.group_id
										},
										schemaId: this.selectedRubric.id
									}
								}
							}).result.then((template: RubricTemplate) => {
								if (!this.isRubricStillSelected(res)) {
									return;
								}

								this.setSelectedRubric(template);
							});
						});
				}
			});
	}

	public handleRemove (): void {
		const selectedRubric = this.getAvailableRubric();
		if (selectedRubric == null) {
			return;
		}

		//if rubric is in use on this activity, don't let it be removed
		this.libraryService.checkRubricUsage(selectedRubric.id, this.rubricRole)
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe((response) => {
				if (!this.isRubricStillSelected(selectedRubric)) {
					return;
				}

				if (response.in_use &&
					response.activities.includes(this.activity.activity_id)) {
					this.matDialog.open(MessageDialogComponent, {
						data: {
							title: this.translate.instant('modal-rubric-delete_title'),
							message: this.translate.instant('modal-rubric-delete_message')
						}
					});
				//else just remove with no confirmation
				} else {
					this.setSelectedRubric(null);
					const fallback: GradingTypeKey = this.gradingTypeLookup.NOT_GRADED.disabled ? 'POINTS' : 'NOT_GRADED';
					this.gradingTypeControl.setValue(fallback);
					this.announceAccessibilityMessage('activity-editor-new_instructor-rubrics-removed');
					this.cdr.markForCheck();
				}
			});
	}

	public openAiContextDialog (): void {
		if (this.aiContextControl == null) {
			return;
		}

		const aiContextControl = this.aiContextControl;
		void import('@go-modules/components/dialogs/ai-rubric-context-dialog/ai-rubric-context-dialog.component')
			.then(({ AiRubricContextDialogComponent }) => {
				if (this.isDestroyed) {
					return;
				}

				AiRubricContextDialogComponent.open(this.matDialog, {
					data: {
						context: aiContextControl.value ?? null
					}
				}).afterClosed()
					.pipe(take(1), takeUntil(this.destroy$))
					.subscribe((context) => {
						if (context === undefined || context === aiContextControl.value) {
							return;
						}

						aiContextControl.markAsDirty();
						aiContextControl.setValue(context);
						this.cdr.markForCheck();
					});
			});
	}

	public hasAiContext (): boolean {
		const value = this.aiContextControl?.value;
		return value != null && value !== '';
	}

	public clearAiContext (): void {
		if (this.aiContextControl == null) {
			return;
		}

		this.aiContextControl.markAsDirty();
		this.aiContextControl.setValue(null);
		this.cdr.markForCheck();
	}

	public getAiContextPreview (): string | null {
		const value = this.aiContextControl?.value;
		if (value == null || value === '') {
			return null;
		}

		const lines = value.split(/\r\n|\r|\n/);
		const preview = lines.slice(0, 3).join('\n');
		return lines.length > 3 ? `${preview}\n...` : preview;
	}

	private determineInitiallySelectedGradingType (): GradingTypeKey {
		if (this.activity?.rubric_template_id && this.activity.rubric_template_id > 0) {
			return 'INSTRUCTOR_RUBRIC';
		}

		if (this.activity?.total_score != null) {
			return 'POINTS';
		}

		return this.gradingTypeLookup.NOT_GRADED.disabled ? 'POINTS' : 'NOT_GRADED';
	}

	private onGradingTypeSelectionChange (newValue: GradingTypeKey): void {
		const previousValue = this.selectedGradingTypeKey;
		if (newValue === previousValue) {
			return;
		}

		if (newValue === 'INSTRUCTOR_RUBRIC' && this.isRubricSelectionUnavailable()) {
			this.restoreGradingType(previousValue);
			this.announceAccessibilityMessage('activity-editor-new_instructor-rubrics-unavailable');
			this.cdr.markForCheck();
			return;
		}

		this.selectedGradingTypeKey = newValue;
		this.handleGradingTypeUpdate();
		this.updatePointsValidators();

		switch (newValue) {
			case 'NOT_GRADED':
				this.setSelectedRubric(null);
				this.setPointsPossible(null);
				break;
			case 'POINTS':
				this.setSelectedRubric(null);
				if (this.activity?.total_score !== null && this.activity?.total_score !== undefined) {
					const parsed = Number(this.activity.total_score);
					this.setPointsPossible(isNaN(parsed) ? null : parsed);
				} else {
					this.setPointsPossible(null);
				}
				break;
			case 'INSTRUCTOR_RUBRIC':
				this.openRubricSelection(previousValue);
				break;
		}

		this.cdr.markForCheck();
	}

	private updatePointsValidators (): void {
		const validators = [this.pointsNumberValidator.bind(this)];
		if (this.selectedGradingTypeKey === 'POINTS') {
			validators.unshift(Validators.required);
		}

		this.pointsControl.setValidators(validators);
		this.pointsControl.updateValueAndValidity({ emitEvent: false });
	}

	private pointsNumberValidator (control: AbstractControl): Record<string, boolean> | null {
		const value = control.value;
		if (value === null || value === undefined || value === '') {
			return null;
		}

		const parsed = Number(value);
		if (isNaN(parsed)) {
			return { invalidNumber: true };
		}

		if (parsed < 0) {
			return { min: true };
		}

		return null;
	}

	private handleGradingTypeUpdate (): void {
		if (this.reviewersAccessControl.value) {
			this.reviewersAccessControl.markAsDirty();
			this.reviewersAccessControl.setValue(false);
		}
	}

	private setPointsPossible (value: number | null, markDirty: boolean = true): void {
		if (markDirty) {
			this.pointsControl.markAsDirty();
		}
		this.pointsControl.setValue(value, { emitEvent: markDirty });
	}

	private setSelectedRubric (rubricTemplate: RubricTemplate | null, markDirty: boolean = true): void {
		this.selectedRubric = rubricTemplate;

		const rubricId = rubricTemplate?.id ?? null;
		if (this.rubricTemplateControl) {
			if (markDirty) {
				this.rubricTemplateControl.markAsDirty();
			}
			this.rubricTemplateControl.setValue(rubricId, { emitEvent: markDirty });
		}

		let numericTotal: number | null = null;
		const total = rubricTemplate?.total;
		if (total !== null && total !== undefined && total !== '') {
			const parsed = Number(total);
			numericTotal = isNaN(parsed) ? null : parsed;
		}

		this.setPointsPossible(numericTotal, markDirty);
		this.syncAutoGradingControlsWithEligibility();
	}

	private openRubricSelection (previousValue: GradingTypeKey): void {
		if (this.isRubricSelectionUnavailable()) {
			this.restoreGradingType(previousValue);
			this.announceAccessibilityMessage('activity-editor-new_instructor-rubrics-unavailable');
			this.cdr.markForCheck();
			return;
		}

		void import('@go-modules/components/dialogs/library-dialog/library-dialog.component')
			.then(({ LibraryDialogComponent }) => {
				if (this.isDestroyed) {
					return;
				}

				this.ngxGoModalService.open(LibraryDialogComponent, false, {
					data: {
						filterType: [CONTENT_TYPES.RUBRICS],
						mode: MODES.SELECT,
						collectionManagement: true,
						...this.getLibraryDialogContextData()
					}
				}).afterClosed()
					.pipe(takeUntil(this.destroy$))
					.subscribe((rubricTemplate: RubricTemplate) => {
						if (rubricTemplate && !isDismissedModalResult(rubricTemplate)) {
							this.setSelectedRubric(rubricTemplate);
							this.announceAccessibilityMessage('activity-editor-new_instructor-rubrics-added');
							this.updatePointsValidators();
							this.cdr.markForCheck();
						} else {
							this.restoreGradingType(previousValue);
							this.cdr.markForCheck();
						}
					});
			});
	}

	private restoreGradingType (previousValue: GradingTypeKey): void {
		this.selectedGradingTypeKey = previousValue;
		this.gradingTypeControl.setValue(previousValue, { emitEvent: false });
		this.updatePointsValidators();
	}

	private getLibraryDialogContextData (): { context: LibraryDialogContext } | Record<string, never> {
		if (this.editorContext == null) {
			return {};
		}

		return {
			context: {
				group: this.editorContext.group?.() ?? null,
				account: this.editorContext.account?.() ?? null,
				org: this.editorContext.org?.() ?? null,
				license: this.editorContext.license?.() ?? null,
				orgSettings: this.editorContext.orgSettings?.() ?? null
			}
		};
	}

	private announceAccessibilityMessage (messageKey: string): void {
		const message = this.translate.instant(messageKey);
		if (this.activityEditAccessibilityAnnouncer != null) {
			this.activityEditAccessibilityAnnouncer.announce(message);
			return;
		}

		void this.liveAnnouncer.announce(message, 'assertive');
	}

	private loadSelectedRubric (rubricId: number): void {
		this.isLoadingRubric = true;
		this.libraryService.getRubricTemplate(rubricId)
			.pipe(
				take(1),
				takeUntil(this.destroy$),
				finalize(() => {
					this.isLoadingRubric = false;
					this.cdr.markForCheck();
				})
			)
			.subscribe({
				next: (rubricTemplate) => {
					this.setSelectedRubric(rubricTemplate, false);
				},
				error: () => {
					this.setSelectedRubric(null, false);
				}
			});
	}

	private get isPointsReadonly (): boolean {
		return !!this.activity.is_points_possible_readonly;
	}

	private hasAdvancedLicenseAccess (): boolean {
		if (this.usesUserAdvancedLicenseEntitlement()) {
			if (!this.advancedLicenseEntitlementResolved()) {
				return false;
			}

			return this.advancedLicenseEntitlement()?.can_use_ai_markers === true;
		}

		return this.getContextLicense()?.salesforce_license?.tier_name === ProductTier.Plus;
	}

	private hasAutoGradingEligibility (): boolean {
		return this.featureFlag.isAvailable('COMBINED_RUBRIC_SCORING') &&
			this.featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG') &&
			this.hasSelectedRubric() &&
			this.hasAdvancedLicenseAccess() &&
			this.isAutoGradingActivityType();
	}

	private hasSelectedRubric (): boolean {
		return this.selectedRubric != null || this.rubricTemplateControl?.value != null;
	}

	private canResolveAutoGradingEligibility (): boolean {
		return !this.usesUserAdvancedLicenseEntitlement() || this.advancedLicenseEntitlementResolved();
	}

	private syncAutoGradingControlsWithEligibility (): void {
		if (!this.hasAutoGradingControls() ||
			!this.canResolveAutoGradingEligibility() ||
			this.hasAutoGradingEligibility()) {
			return;
		}

		this.clearAutoGradingControls();
	}

	private hasAutoGradingControls (): boolean {
		return this.autoGradingControl != null &&
			this.autoGradingUseAIScoreControl != null &&
			this.autoGradingAllowReviewerOverrideControl != null &&
			this.autoGradingAutoPostAIRubricControl != null;
	}

	private clearAutoGradingControls (): void {
		const controls = [
			this.autoGradingControl,
			this.autoGradingUseAIScoreControl,
			this.autoGradingAllowReviewerOverrideControl,
			this.autoGradingAutoPostAIRubricControl
		];

		controls.forEach((control) => this.setControlValueIfChanged(control, false));
	}

	private setControlValueIfChanged (control: FormControl | null, value: boolean): void {
		if (control == null) {
			return;
		}

		if (control.value !== value) {
			control.setValue(value);
		}
	}

	private isAutoGradingActivityType (): boolean {
		const templateType = this.getSelectedTemplateType();

		return templateType == null || !AUTO_GRADING_UNSUPPORTED_TEMPLATE_TYPES.includes(templateType);
	}

	private getSelectedTemplateType (): string | null {
		const template = this.selectedTemplate ?? this.activity?.template;

		return template?.getType?.() ?? template?.slug ?? null;
	}

	private updateUserAdvancedLicenseEntitlement (): void {
		if (!this.usesUserAdvancedLicenseEntitlement()) {
			this.advancedLicenseEntitlement.set(null);
			this.advancedLicenseEntitlementResolved.set(false);
			return;
		}

		if (this.advancedLicenseEntitlementResolved()) {
			return;
		}

		this.advancedLicenseEntitlement.set(null);
		this.advancedLicenseEntitlementResolved.set(false);

		this.ngxUserService.getAiMarkersEntitlement()
			.pipe(
				take(1),
				catchError(() => of({
					can_use_ai_markers: false,
					license: null,
					llm_definitions: []
				})),
				takeUntil(this.destroy$)
			)
			.subscribe((entitlement) => {
				this.advancedLicenseEntitlement.set(entitlement);
				this.advancedLicenseEntitlementResolved.set(true);
				this.syncAutoGradingControlsWithEligibility();
				this.cdr.markForCheck();
			});
	}

	private usesUserAdvancedLicenseEntitlement (): boolean {
		return this.editorContext?.environment?.()?.name === 'library';
	}

	private getContextLicense (): License | null {
		if (this.editorContext != null) {
			return (this.editorContext.license?.() ?? null) as License | null;
		}

		return this.getGoSettings().currentFolder?.license ?? null;
	}

	private getGoSettings (): ActivityEditorGoSettings {
		return (window as Window & { goSettings: ActivityEditorGoSettings }).goSettings;
	}

	private getAvailableRubric (): RubricTemplate | null {
		if (this.isLoadingRubric) {
			return null;
		}

		return this.selectedRubric;
	}

	private isRubricStillSelected (rubric: RubricTemplate): boolean {
		return !this.isDestroyed && this.selectedRubric?.id === rubric.id;
	}
}
